import cn from 'classnames';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import './FiltersPanel.scss';
import { Button } from '../../Components/Button';
import { Design, Size } from '../../Components/designConstants';
import { InteractiveDiv } from '../../Components/InteractiveEl';

interface FiltersPanelProps {
  ageMap: Partial<Record<string, boolean>>;
  setAgeMap: (key: string, value: boolean) => void;
  genderMap: Partial<Record<string, boolean>>;
  setGenderMap: (key: string, value: boolean) => void;
  regionMap: Partial<Record<string, boolean>>;
  setRegionMap: (key: string, value: boolean) => void;
  /**
   * The regions this advertiser's data contains, from getAdvertiserInfo. Empty until that response
   * arrives, in which case only "All" is offered — which is correct rather than merely safe, since
   * "All" sends no region filter at all. Never a hardcoded list: the labels are per country, and a
   * copy of them here is what excluded every Canadian row from its own chart.
   */
  regionLabels: readonly string[];
  incomeMap: Partial<Record<string, boolean>>;
  setIncomeMap: (key: string, value: boolean) => void;
  setFetchingData: Dispatch<SetStateAction<boolean>>;
  appliedFilters: Record<string, boolean | undefined>;
  inLoadingState: boolean;
}

export const FiltersPanel = ({
  ageMap,
  setAgeMap,
  genderMap,
  setGenderMap,
  regionMap,
  setRegionMap,
  regionLabels,
  incomeMap,
  setIncomeMap,
  setFetchingData,
  appliedFilters,
  inLoadingState
}: FiltersPanelProps) => {
  const resetFilters = useCallback(() => {
    setAgeMap('Age-All', true);
    setAgeMap('18-34', false);
    setAgeMap('35-49', false);
    setAgeMap('50+', false);
    setGenderMap('Gender-All', true);
    setGenderMap('Male', false);
    setGenderMap('Female', false);
    setRegionMap('Region-All', true);
    for (const region of regionLabels) {
      setRegionMap(region, false);
    }
    setIncomeMap('All', true);
    setIncomeMap('Under 40K', false); /* deprecated */
    setIncomeMap('40K - 80K', false); /* deprecated */
    setIncomeMap('80K - 120K', false); /* deprecated */
    setIncomeMap('Over 120K', false); /* deprecated */
    setIncomeMap('Under 25K', false);
    setIncomeMap('25K - 100K', false);
    setIncomeMap('Over 100K', false);
  }, [regionLabels, setAgeMap, setGenderMap, setIncomeMap, setRegionMap]);

  const inDefaultState = useMemo(
    () => ageMap['Age-All'] && genderMap['Gender-All'] && regionMap['Region-All'] && incomeMap.All,
    [ageMap, genderMap, incomeMap, regionMap]
  );

  const selectedFilters = useMemo(
    () => ({ ...ageMap, ...genderMap, ...regionMap, ...incomeMap }),
    [ageMap, genderMap, incomeMap, regionMap]
  );

  const areFiltersApplied = useMemo(() => {
    if (Object.keys(appliedFilters).length !== Object.keys(selectedFilters).length) {
      return false;
    }

    let output = true;
    Object.keys(appliedFilters).forEach(key => {
      if (selectedFilters[key] !== appliedFilters[key]) {
        output = false;
      }
    });
    return output;
  }, [appliedFilters, selectedFilters]);

  const allowRefresh = useMemo(() => {
    let output = false;
    Object.keys(appliedFilters).forEach(key => {
      if (
        key !== 'Age-All' &&
        key !== 'Gender-All' &&
        key !== 'Region-All' &&
        key !== 'All' &&
        appliedFilters[key] === true
      ) {
        output = true;
      }
    });

    return output;
  }, [appliedFilters]);

  const [innerHeight, setInnerHeight] = useState(window.innerHeight);
  useEffect(() => {
    const handleResize = () => setInnerHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  let spacingClass: string | undefined;
  if (innerHeight < 972 && innerHeight >= 888) {
    spacingClass = 'reduce-spacing-1';
  } else if (innerHeight < 888 && innerHeight >= 804) {
    spacingClass = 'reduce-spacing-2';
  } else if (innerHeight < 804 && innerHeight >= 720) {
    spacingClass = 'reduce-spacing-3';
  } else if (innerHeight < 720) {
    spacingClass = 'wrap';
  }

  const resetDisabled = inDefaultState || inLoadingState;
  const applyDisabled = areFiltersApplied || inLoadingState;

  return (
    <div className='bhm-filters-panel'>
      <div className='header'>
        <div className='title'>Filters</div>
      </div>
      <div className={cn('bhm-filters', spacingClass)}>
        <div className={cn('bhm-filter-section', spacingClass)}>
          <div className='bhm-filter-name'>Age</div>
          <div className={cn('bhm-filter-options', spacingClass)}>
            <CheckBox
              checked={ageMap['Age-All'] || false}
              label='All'
              onCheck={() => {
                setAgeMap('Age-All', !ageMap['Age-All']);
                setAgeMap('18-34', false);
                setAgeMap('35-49', false);
                setAgeMap('50+', false);
              }}
            />
            <CheckBox
              checked={ageMap['18-34'] || false}
              label='18–34'
              onCheck={() => {
                setAgeMap('18-34', !ageMap['18-34']);
                setAgeMap('Age-All', false);
              }}
            />
            <CheckBox
              checked={ageMap['35-49'] || false}
              label='35–49'
              onCheck={() => {
                setAgeMap('35-49', !ageMap['35-49']);
                setAgeMap('Age-All', false);
              }}
            />
            <CheckBox
              checked={ageMap['50+'] || false}
              label='50+'
              onCheck={() => {
                setAgeMap('50+', !ageMap['50+']);
                setAgeMap('Age-All', false);
              }}
            />
          </div>
        </div>
        <div className={cn('bhm-filter-section', spacingClass)}>
          <div className='bhm-filter-name'>Gender</div>
          <div className={cn('bhm-filter-options', spacingClass)}>
            <CheckBox
              checked={genderMap['Gender-All'] || false}
              label='All'
              onCheck={() => {
                setGenderMap('Gender-All', !genderMap['Gender-All']);
                setGenderMap('Male', false);
                setGenderMap('Female', false);
              }}
            />
            <CheckBox
              checked={genderMap.Male || false}
              label='Male'
              onCheck={() => {
                setGenderMap('Male', !genderMap.Male);
                setGenderMap('Gender-All', false);
              }}
            />
            <CheckBox
              checked={genderMap.Female || false}
              label='Female'
              onCheck={() => {
                setGenderMap('Female', !genderMap.Female);
                setGenderMap('Gender-All', false);
              }}
            />
          </div>
        </div>
        <div className={cn('bhm-filter-section', spacingClass)}>
          <div className='bhm-filter-name'>Region</div>
          <div className={cn('bhm-filter-options', spacingClass)}>
            <CheckBox
              checked={regionMap['Region-All'] || false}
              label='All'
              onCheck={() => {
                setRegionMap('Region-All', !regionMap['Region-All']);
                for (const region of regionLabels) {
                  setRegionMap(region, false);
                }
              }}
            />
            {regionLabels.map(region => (
              <CheckBox
                key={region}
                checked={regionMap[region] || false}
                label={region}
                onCheck={() => {
                  setRegionMap(region, !regionMap[region]);
                  setRegionMap('Region-All', false);
                }}
              />
            ))}
          </div>
        </div>
        <div className={cn('bhm-filter-section', spacingClass)}>
          <div className='bhm-filter-name'>Household Income</div>
          <div className={cn('bhm-filter-options', spacingClass)}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <CheckBox
                checked={incomeMap.All || false}
                label='All'
                onCheck={() => {
                  setIncomeMap('All', !incomeMap.All);
                  setIncomeMap('Under 40K', false); /* deprecated */
                  setIncomeMap('40K - 80K', false); /* deprecated */
                  setIncomeMap('80K - 120K', false); /* deprecated */
                  setIncomeMap('Over 120K', false); /* deprecated */
                  setIncomeMap('Under 25K', false);
                  setIncomeMap('25K - 100K', false);
                  setIncomeMap('Over 100K', false);
                }}
              />
              <span
                style={{
                  paddingLeft: '7px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '14px'
                }}
              >
                (includes participants who prefer not to say)
              </span>
            </div>
            <CheckBox
              checked={incomeMap['Under 25K'] || false}
              label='<$25k'
              onCheck={() => {
                setIncomeMap('Under 25K', !incomeMap['Under 25K']);
                setIncomeMap('All', false);
              }}
            />
            <CheckBox
              /* deprecated */
              checked={incomeMap['Under 40K'] || false}
              label='<$40k'
              onCheck={() => {
                setIncomeMap('Under 40K', !incomeMap['Under 40K']);
                setIncomeMap('All', false);
              }}
            />
            <CheckBox
              /* deprecated */
              checked={incomeMap['40K - 80K'] || false}
              label='$40–$80k'
              onCheck={() => {
                setIncomeMap('40K - 80K', !incomeMap['40K - 80K']);
                setIncomeMap('All', false);
              }}
            />
            <CheckBox
              checked={incomeMap['25K - 100K'] || false}
              label='$25–$100k'
              onCheck={() => {
                setIncomeMap('25K - 100K', !incomeMap['25K - 100K']);
                setIncomeMap('All', false);
              }}
            />
            <CheckBox
              /* deprecated */
              checked={incomeMap['80K - 120K'] || false}
              label='$80–$120k'
              onCheck={() => {
                setIncomeMap('80K - 120K', !incomeMap['80K - 120K']);
                setIncomeMap('All', false);
              }}
            />
            <CheckBox
              checked={incomeMap['Over 100K'] || false}
              label='>$100k'
              onCheck={() => {
                setIncomeMap('Over 100K', !incomeMap['Over 100K']);
                setIncomeMap('All', false);
              }}
            />
            <CheckBox
              /* deprecated */
              checked={incomeMap['Over 120K'] || false}
              label='>$120k'
              onCheck={() => {
                setIncomeMap('Over 120K', !incomeMap['Over 120K']);
                setIncomeMap('All', false);
              }}
            />
          </div>
        </div>
      </div>
      <div className='bhm-actions'>
        <Button
          size={Size.LG}
          design={Design.NEUTRAL}
          disabled={resetDisabled}
          tooltip={
            inDefaultState && !inLoadingState
              ? { text: 'The filter selection is already set to the defualt.' }
              : undefined
          }
          onClick={() => {
            resetFilters();
            if (allowRefresh) {
              setFetchingData(true);
            }
          }}
        >
          Reset Filters
        </Button>
        <Button
          size={Size.LG}
          disabled={applyDisabled}
          tooltip={
            areFiltersApplied && !inLoadingState
              ? { text: 'The selected filters are already applied.' }
              : undefined
          }
          onClick={() => {
            setFetchingData(true);
          }}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

interface CheckBoxProps {
  checked: boolean;
  label: string;
  onCheck: (key: string, value: boolean) => void;
}

const CheckBox = ({ checked, label, onCheck }: CheckBoxProps) => (
  <div className='checkbox-container'>
    <InteractiveDiv
      className={cn('checkbox', { checked })}
      onClick={() => onCheck(label, !checked)}
    />
    <div className={cn('label', { checked })}>{label}</div>
  </div>
);
